/* AMD Contact form — accessible validation for both transports.

   Native mode (no data-endpoint): the form POSTs to Shopify's /contact as
   usual. This component only replaces the browser's one-bubble-at-a-time
   validation with a per-field message, aria-invalid and an aria-live summary,
   then focuses the first offending field. Without JS, the browser's own
   `required` handling plus Shopify's server validation still guard the submit.

   Endpoint mode (data-endpoint set): the same validation runs, then the
   component POSTs FormData and expects {ok:true} / {ok:false,message}. Used for
   forms with file fields, which Shopify's contact form cannot carry. */

class AmdContactForm extends HTMLElement {
  constructor() {
    super();
    this._onSubmit = this._onSubmit.bind(this);
    this._onInput = this._onInput.bind(this);
    this._onFileChange = this._onFileChange.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.statusEl = this.querySelector('[data-amd-cf-status]');
    this.successEl = this.querySelector('[data-amd-cf-success]');
    this.endpoint = this.dataset.endpoint || '';
    if (!this.form) return;

    this.submitButton = this.form.querySelector('[type="submit"]');
    this.honeypot = this.form.querySelector('[name="website"]');

    this.form.addEventListener('submit', this._onSubmit);
    this.form.addEventListener('input', this._onInput);
    this.form.addEventListener('change', this._onInput);
    this.querySelectorAll('[data-amd-cf-file]').forEach((input) => {
      input.addEventListener('change', this._onFileChange);
    });

    // A server-rendered banner should announce itself after the page reload.
    const banner = this.querySelector('[data-amd-cf-server-error]');
    if (banner) banner.focus();
    else if (this.successEl && !this.successEl.hidden) this.successEl.focus();
  }

  disconnectedCallback() {
    if (!this.form) return;

    this.form.removeEventListener('submit', this._onSubmit);
    this.form.removeEventListener('input', this._onInput);
    this.form.removeEventListener('change', this._onInput);
    this.querySelectorAll('[data-amd-cf-file]').forEach((input) => {
      input.removeEventListener('change', this._onFileChange);
    });
  }

  /* ---------------------------------------------------------------- helpers */

  _wrapper(el) {
    return el.closest('.amd-cf__field');
  }

  _errorEl(el) {
    const wrapper = this._wrapper(el);
    return wrapper ? wrapper.querySelector('[data-amd-cf-error]') : null;
  }

  _clear(el) {
    el.removeAttribute('aria-invalid');
    const errorEl = this._errorEl(el);
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
  }

  _mark(el, message) {
    el.setAttribute('aria-invalid', 'true');
    const errorEl = this._errorEl(el);
    if (errorEl) {
      errorEl.textContent = message || el.validationMessage || 'This field is required.';
      errorEl.hidden = false;
    }
  }

  _setStatus(message) {
    if (!this.statusEl) return;

    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = '';
      return;
    }

    this.statusEl.textContent = message;
    this.statusEl.hidden = false;
  }

  _plainFields() {
    return Array.from(this.form.querySelectorAll('input, textarea, select')).filter(
      (el) =>
        el.type !== 'checkbox' &&
        el.type !== 'file' &&
        el.type !== 'hidden' &&
        el.type !== 'submit' &&
        el.name !== 'website'
    );
  }

  _groupHasCheck(fieldset) {
    return Array.from(fieldset.querySelectorAll('input[type="checkbox"]')).some((b) => b.checked);
  }

  /* ------------------------------------------------------------------ files */

  /* `accept` may hold MIME types (image/jpeg), wildcards (video/*) or
     extensions (.mov) — the legacy form used extensions. Matching on `type`
     alone rejected valid files, because the browser reports an empty or
     vendor-specific type for several video containers. */
  _fileMatchesAccept(file, tokens) {
    const type = (file.type || '').toLowerCase();
    const name = file.name.toLowerCase();

    return tokens.some((token) => {
      if (token.startsWith('.')) return name.endsWith(token);
      if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
      return type === token;
    });
  }

  /* The pickers live in a fieldset: "at least one" is a group rule, and size
     and type are per file. Returns a message or null. */
  _fileGroupProblem(group) {
    const inputs = Array.from(group.querySelectorAll('input[type="file"]'));
    const files = inputs.flatMap((input) => Array.from(input.files || []));
    const maxMb = parseInt(group.dataset.maxSizeMb, 10) || 0;
    const maxBytes = maxMb * 1024 * 1024;
    const accepted = (inputs[0]?.getAttribute('accept') || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    if (group.dataset.required === 'true' && files.length === 0) {
      return group.dataset.requiredMessage || 'Please attach at least one file.';
    }

    const tooBig = files.find((f) => maxBytes && f.size > maxBytes);
    if (tooBig) return `"${tooBig.name}" is over ${maxMb}MB.`;

    const wrongType =
      accepted.length && files.find((f) => !this._fileMatchesAccept(f, accepted));
    if (wrongType) return `"${wrongType.name}" is not an accepted file type.`;

    return null;
  }

  _clearGroupError(group) {
    group.removeAttribute('aria-invalid');
    const wrapper = group.closest('.amd-cf__field');
    const errorEl = wrapper ? wrapper.querySelector('[data-amd-cf-error]') : null;
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
  }

  _markGroupError(group, message) {
    group.setAttribute('aria-invalid', 'true');
    const wrapper = group.closest('.amd-cf__field');
    const errorEl = wrapper ? wrapper.querySelector('[data-amd-cf-error]') : null;
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  _onFileChange(event) {
    const group = event.currentTarget.closest('[data-amd-cf-file-group]');
    if (!group) return;

    const problem = this._fileGroupProblem(group);
    if (problem) this._markGroupError(group, problem);
    else this._clearGroupError(group);
  }

  /* ------------------------------------------------------------ validation */

  _onInput(event) {
    const field = event.target;

    if (field.type === 'checkbox') {
      if (field.hasAttribute('required') && !field.closest('[data-amd-cf-require-one]')) {
        if (field.checkValidity()) this._clear(field);
        return;
      }
      const fieldset = field.closest('[data-amd-cf-require-one]');
      if (fieldset && this._groupHasCheck(fieldset)) {
        fieldset.removeAttribute('aria-invalid');
        this._clear(fieldset);
      }
      return;
    }

    if (field.type === 'file') return; // handled by _onFileChange

    if (field.hasAttribute('aria-invalid') && field.checkValidity()) this._clear(field);
  }

  _validate() {
    let firstInvalid = null;

    this._plainFields().forEach((el) => {
      if (el.checkValidity()) {
        this._clear(el);
      } else {
        this._mark(el);
        if (!firstInvalid) firstInvalid = el;
      }
    });

    // Single required checkboxes (consent). Groups are handled below.
    this.form.querySelectorAll('input[type="checkbox"][required]').forEach((box) => {
      if (box.closest('[data-amd-cf-require-one]')) return;
      if (box.checkValidity()) {
        this._clear(box);
      } else {
        this._mark(box, 'Please check this box to continue.');
        if (!firstInvalid) firstInvalid = box;
      }
    });

    this.querySelectorAll('[data-amd-cf-file-group]').forEach((group) => {
      const problem = this._fileGroupProblem(group);
      if (problem) {
        this._markGroupError(group, problem);
        if (!firstInvalid) firstInvalid = group.querySelector('input[type="file"]');
      } else {
        this._clearGroupError(group);
      }
    });

    this.querySelectorAll('[data-amd-cf-require-one]').forEach((fieldset) => {
      if (this._groupHasCheck(fieldset)) {
        fieldset.removeAttribute('aria-invalid');
        this._clear(fieldset);
      } else {
        fieldset.setAttribute('aria-invalid', 'true');
        this._mark(fieldset, 'Please select at least one option.');
        if (!firstInvalid) firstInvalid = fieldset.querySelector('input[type="checkbox"]');
      }
    });

    return firstInvalid;
  }

  /* ---------------------------------------------------------------- submit */

  _onSubmit(event) {
    // Prevented immediately, before any validation runs: in endpoint mode the
    // browser must never post this form natively. Waiting until after
    // validation left a gap — a thrown error there would skip preventDefault()
    // and the browser would submit for real, reloading the page.
    if (this.endpoint) event.preventDefault();

    const firstInvalid = this._validate();

    if (firstInvalid) {
      if (!this.endpoint) event.preventDefault();
      this._setStatus(this.dataset.validationText || 'Please complete all required fields.');
      firstInvalid.focus();
      return;
    }

    this._setStatus('');

    // Native mode: let the browser post to Shopify.
    if (!this.endpoint) return;

    // A filled honeypot means a bot. Show success without posting, so the
    // script learns nothing about which fields matter.
    if (this.honeypot && this.honeypot.value !== '') {
      this._showSuccess();
      return;
    }

    // Rebuild the payload rather than posting the raw form: the endpoint drops
    // blank text and empty files anyway, and the contract asks us not to send
    // them, so a field the visitor left empty never leaves the browser. The
    // honeypot (`website`) is dropped the same way.
    const payload = new FormData();
    for (const [key, value] of new FormData(this.form).entries()) {
      if (key === 'website') continue;
      if (typeof value === 'string') {
        if (value.trim() === '') continue;
        payload.append(key, value);
      } else {
        if (value.size === 0 && !value.name) continue;
        payload.append(key, value);
      }
    }

    this._setStatus(this.dataset.sendingText || 'Sending...');
    if (this.submitButton) this.submitButton.disabled = true;

    fetch(this.endpoint, { method: 'POST', body: payload })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then((result) => {
        if (result.ok && result.data && result.data.ok) {
          this._setStatus('');
          this._showSuccess();
        } else {
          this._setStatus((result.data && result.data.message) || this.dataset.errorText);
        }
      })
      .catch(() => {
        this._setStatus(this.dataset.errorText || 'Something went wrong. Please try again.');
      })
      .finally(() => {
        if (this.submitButton) this.submitButton.disabled = false;
      });
  }

  _showSuccess() {
    this.form.hidden = true;
    if (!this.successEl) return;

    this.successEl.hidden = false;
    this.successEl.focus();
  }
}

if (!customElements.get('amd-contact-form')) {
  customElements.define('amd-contact-form', AmdContactForm);
}
